import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { SeatBarcodeInput } from 'src/components/UploadArtifacts/UploadBarcodes/SeatBarcodeInput';
import { WarningMessage } from 'src/core/POS/MessageWithIcon';
import { vars } from 'src/core/themes';
import { DividerLine } from 'src/core/ui';

import {
  BulkBarcodeForm,
  TicketBarcodeWithEntityId,
} from '../UploadBarcodes.utils';

export const BulkManualBarcodeEntry = ({
  readOnly = false,
}: {
  readOnly?: boolean;
}) => {
  const {
    watch,
    trigger,
    setValue,
    formState: { isDirty, defaultValues },
  } = useFormContext<BulkBarcodeForm>();
  const [showErrors, setShowErrors] = useState<boolean>(isDirty);
  const barcodes = watch('barcodes');

  useEffect(() => {
    // Handle toggle between bulk and manual where extra barcodes were entered
    const hasRemovedBarcodes = barcodes.some((b, i) => {
      return (
        !b.barcode.trim() && !!defaultValues?.barcodes?.[i]?.barcode?.trim()
      );
    });

    if (hasRemovedBarcodes) {
      setValue(
        'barcodes',
        barcodes.map((barcode: TicketBarcodeWithEntityId, index) => ({
          ...barcode,
          barcode:
            (barcode.barcode.trim() ||
              defaultValues?.barcodes?.[index]?.barcode) ??
            '', // Don't allow removing existing barcodes
        }))
      );
    }

    if (isDirty) {
      // loaded from a toggle, show errors
      setShowErrors(true);
      trigger();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {barcodes.map(
        (ticketBarcode: TicketBarcodeWithEntityId, index: number) => {
          if (
            index > 0 &&
            ticketBarcode.entityId !== barcodes[index - 1].entityId
          ) {
            return (
              <>
                <DividerLine
                  key={`${ticketBarcode.ticketId}-divider`}
                  style={{
                    margin: `0 ${vars.spacing.sm}`,
                    borderStyle: 'dashed',
                  }}
                />
                <BulkManualBarcodeInput
                  key={ticketBarcode.ticketId}
                  ticketBarcode={ticketBarcode}
                  disabled={readOnly}
                  index={index}
                  forceShowErrors={showErrors}
                />
              </>
            );
          }

          return (
            <BulkManualBarcodeInput
              key={ticketBarcode.ticketId}
              ticketBarcode={ticketBarcode}
              disabled={readOnly}
              index={index}
              forceShowErrors={showErrors}
            />
          );
        }
      )}
    </>
  );
};

const BulkManualBarcodeInput = ({
  ticketBarcode,
  disabled,
  index,
}: {
  ticketBarcode: TicketBarcodeWithEntityId;
  disabled: boolean;
  index: number;
  forceShowErrors?: boolean;
}) => {
  const { register, formState } = useFormContext<BulkBarcodeForm>();

  const field: `barcodes.${number}.barcode` = `barcodes.${index}.barcode`;

  const defaultBarcodeValue =
    formState.defaultValues?.barcodes?.[index]?.barcode;

  const error = formState.errors.barcodes?.[index]?.barcode;
  const warning = error?.type === 'warning' ? error?.message : '';
  const errorMessage = error?.type !== 'warning' ? (error?.message ?? '') : '';

  return (
    <>
      <SeatBarcodeInput
        ticketBarcode={ticketBarcode}
        index={index}
        disabled={disabled}
        errors={errorMessage ? [errorMessage] : undefined}
        defaultValue={defaultBarcodeValue}
        {...register(field)}
      />
      {warning && <WarningMessage message={warning} />}
    </>
  );
};
